import { useEffect, useState } from 'react';
import { api, ApiError, type OfficeCard as Card, type OfficeSummary } from '../api';
import { date, money, nip, type Dictionary, type Locale } from '../i18n';
import { AgreementStatus } from './Companies';
import * as Icon from '@firmeto/design/Icons';
import { IndividualAgreements } from './IndividualAgreements';

/**
 * Biura rachunkowe z zewnątrz (E11-S7) — przeglądanie.
 *
 * Operator odpowiada na pytania „kto prowadzi księgi tej firmy”, „za które firmy płaci biuro”
 * i „dlaczego biuro dostało taką fakturę”. Nie widzi ksiąg klientów i nie zmienia umów — umowa
 * obsługi wymaga zgody firmy (E2-S2) i należy do tych dwóch stron. Liczniki na liście mówią
 * o dziś; karta pokazuje też historię, bo o nią operator pyta najczęściej.
 */

function failureText(failure: unknown): string {
  return failure instanceof ApiError ? failure.message : String(failure);
}

export function Offices({ t, onOpen }: { t: Dictionary; onOpen: (officeId: string) => void }) {
  const [query, setQuery] = useState('');
  const [items, setItems] = useState<OfficeSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Jak przy firmach: lista od razu, zapytanie zawęża po chwili zwłoki
  useEffect(() => {
    const timer = setTimeout(() => {
      api
        .offices(query)
        .then(setItems)
        .catch((failure) => setError(failureText(failure)));
    }, 250);
    return () => clearTimeout(timer);
  }, [query]);

  return (
    <>
      <div className="hd">
        <div>
          <h3>{t.offices.title}</h3>
          <p>{t.offices.lead}</p>
        </div>
      </div>

      <div className="panel">
        <label className="fld">
          <span>{t.offices.search}</span>
          <input className="inp" autoFocus value={query} placeholder="123-456-78-90" onChange={(event) => setQuery(event.target.value)} />
        </label>
        {items && <p className="hint">{t.offices.count(items.length)}</p>}
      </div>

      {error && (
        <div className="alert a-bad">
          <Icon.Error />
          <div>
            <b>{error}</b>
          </div>
        </div>
      )}

      {items === null && <p className="muted">{t.loading}</p>}
      {items && items.length === 0 && (
        <div className="panel">
          <p>{t.offices.none}</p>
        </div>
      )}

      {items && items.length > 0 && (
        <div className="panel">
          <div className="tbl">
            <table>
              <thead>
                <tr>
                  <th className="wrap">{t.offices.name}</th>
                  <th>{t.offices.nip}</th>
                  <th>{t.offices.package}</th>
                  <th className="n">{t.offices.members}</th>
                  <th className="n">{t.offices.clients}</th>
                  <th className="n">{t.offices.paidByOffice}</th>
                  <th className="n">{t.offices.pending}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((office) => (
                  <tr key={office.id}>
                    <td className="wrap">
                      <button type="button" className="btn ghost sm" onClick={() => onOpen(office.id)}>
                        <b>{office.name}</b>
                      </button>
                    </td>
                    <td className="mono">{nip(office.nip)}</td>
                    <td>{t.offices.packages[office.packageCode] ?? office.packageCode}</td>
                    <td className="n mono">{office.members}</td>
                    <td className="n mono">{office.clients}</td>
                    <td className="n mono">{office.paidByOffice}</td>
                    <td className="n mono">{office.pending > 0 ? <span className="chip st-warn">{office.pending}</span> : '0'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  );
}

/** Karta biura: zespół, umowy z firmami (także czekające i zakończone) i miesiące na rachunek biura. */
export function OfficeCard({
  officeId,
  t,
  locale,
  onBack,
  onCompany,
}: {
  officeId: string;
  t: Dictionary;
  locale: Locale;
  onBack: () => void;
  onCompany: (companyId: string) => void;
}) {
  const [card, setCard] = useState<Card | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .office(officeId)
      .then(setCard)
      .catch((failure) => setError(failureText(failure)));
  }, [officeId]);

  if (!card) {
    return (
      <>
        {error && (
          <div className="alert a-bad">
            <Icon.Error />
            <div>
              <b>{error}</b>
            </div>
          </div>
        )}
        {!error && <p className="muted">{t.loading}</p>}
      </>
    );
  }

  const { office } = card;

  return (
    <>
      <div className="hd">
        <div>
          <h3>{office.name}</h3>
          <p>
            <span className="mono">{nip(office.nip)}</span> · {t.offices.package}: {t.offices.packages[office.packageCode] ?? office.packageCode} ·{' '}
            {t.offices.since} {date(office.createdAt, locale)}
          </p>
        </div>
        <div className="sp">
          <button type="button" className="btn ghost" onClick={onBack}>
            {t.offices.back}
          </button>
        </div>
      </div>

      <div className="panel">
        <div className="hd">
          <div>
            <h4>{t.offices.agreements}</h4>
          </div>
        </div>
        {card.agreements.length === 0 ? (
          <p className="muted">{t.offices.noAgreements}</p>
        ) : (
          <div className="tbl">
            <table>
              <thead>
                <tr>
                  <th className="wrap">{t.offices.company}</th>
                  <th>{t.offices.nip}</th>
                  <th>{t.offices.payer}</th>
                  <th className="wrap">{t.offices.status}</th>
                  <th>{t.offices.from}</th>
                  <th>{t.offices.to}</th>
                  <th className="wrap">{t.offices.accountants}</th>
                </tr>
              </thead>
              <tbody>
                {card.agreements.map((agreement) => (
                  <tr key={agreement.id}>
                    <td className="wrap">
                      <button type="button" className="btn ghost sm" onClick={() => onCompany(agreement.companyId)}>
                        <b>{agreement.companyName}</b>
                      </button>
                    </td>
                    <td className="mono">{nip(agreement.companyNip)}</td>
                    <td>{t.offices.payers[agreement.payer]}</td>
                    <td className="wrap">
                      <AgreementStatus agreement={agreement} t={t} />
                    </td>
                    <td>{date(agreement.validFrom, locale)}</td>
                    <td>{agreement.validTo ? date(agreement.validTo, locale) : t.offices.stillActive}</td>
                    <td className="wrap">{agreement.accountants.join(', ') || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="grid two">
        <div className="panel">
          <div className="hd">
            <div>
              <h4>{t.offices.team}</h4>
            </div>
          </div>
          <div className="tbl">
            <table>
              <thead>
                <tr>
                  <th className="wrap">{t.companies.name}</th>
                  <th>{t.offices.role}</th>
                  <th>{t.offices.from}</th>
                  <th>{t.offices.to}</th>
                </tr>
              </thead>
              <tbody>
                {card.members.map((member) => (
                  <tr key={`${member.email}-${member.role}-${member.validFrom}`}>
                    <td className="wrap">
                      {member.fullName}
                      <div className="hint">{member.email}</div>
                    </td>
                    <td>{t.offices.roles[member.role]}</td>
                    <td>{date(member.validFrom, locale)}</td>
                    <td>{member.validTo ? date(member.validTo, locale) : t.offices.stillActive}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="panel">
          <div className="hd">
            <div>
              <h4>{t.offices.months}</h4>
              <p>{t.offices.monthsLead}</p>
            </div>
          </div>
          {card.months.length === 0 ? (
            <p className="muted">{t.offices.noMonths}</p>
          ) : (
            <div className="tbl">
              <table>
                <thead>
                  <tr>
                    <th>{t.credits.period}</th>
                    <th className="n">{t.offices.companiesCount}</th>
                    <th className="n">{t.offices.revenue}</th>
                    <th className="n">{t.offices.fee}</th>
                    <th className="n">{t.offices.ceiling}</th>
                    <th className="n">{t.offices.agreementFee}</th>
                    <th className="n">{t.offices.charged}</th>
                  </tr>
                </thead>
                <tbody>
                  {card.months.map((month) => (
                    <tr key={month.period}>
                      <td className="mono">{month.period}</td>
                      <td className="n mono">{month.companies}</td>
                      <td className="n mono">{money(month.revenue, locale)}</td>
                      <td className="n mono">{money(month.feeGross, locale)}</td>
                      <td className="n mono">{month.ceilingGross ? money(month.ceilingGross, locale) : '—'}</td>
                      <td className="n mono">{Number(month.agreementFeeGross) > 0 ? money(month.agreementFeeGross, locale) : '—'}</td>
                      <td className="n mono">
                        <b>{money(month.chargedGross, locale)}</b>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <IndividualAgreements subject={{ kind: 'offices', id: officeId }} t={t} locale={locale} />
    </>
  );
}
